import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from './authStore';
import { hasAccess, type AccessRequirement } from './roles';

/** Redirects to /login when there is no session. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const authenticated = useAuthStore((s) => s.accessToken !== null && s.user !== null);
  const location = useLocation();
  if (!authenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children ? <>{children}</> : <Outlet />;
}

/** Renders children only when the session satisfies the role/cap requirement; otherwise a 403 panel. */
export function RequireAccess({ children, ...req }: AccessRequirement & { children?: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const { t } = useTranslation();
  if (!user || !hasAccess(user.roles, user.caps, req)) {
    return (
      <div role="alert" className="mx-auto mt-16 max-w-md text-center">
        <h1 className="text-lg font-semibold">{t('errors.forbiddenTitle')}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t('errors.forbiddenBody')}</p>
      </div>
    );
  }
  return children ? <>{children}</> : <Outlet />;
}
